/**
 * Juwelier Reichart · lebende Hausuhr im Einstieg (snippets/jr-uhr.liquid).
 * Zeiger zeigen die Zeit der angegebenen Zeitzone; der Sekundenzeiger gleitet wie bei einem Automatikwerk.
 * Mit Maus kippt die Uhr leicht in Richtung Zeiger. Bei reduzierter Bewegung springt nur der Minutenzeiger, ohne Kippen.
 * Bei jedem Seitenaufruf zeigt sie eines von zehn Modellen (assets/jr-uhr-modelle.js), nie zweimal hintereinander dasselbe;
 * ?uhr=1 … 10 erzwingt ein Modell. Ohne JavaScript bleibt das gerenderte Modell 1 stehen.
 */

const MERKER = 'jr-uhr-modell';

/** @param {number} anzahl */
function modellWaehlen(anzahl) {
  const wunsch = Number(new URLSearchParams(location.search).get('uhr'));
  if (wunsch >= 1 && wunsch <= anzahl) return wunsch - 1;
  let zuletzt = -1;
  try {
    zuletzt = Number(sessionStorage.getItem(MERKER) ?? -1);
  } catch {}
  let wahl = Math.floor(Math.random() * (anzahl - 1));
  if (wahl >= zuletzt && zuletzt >= 0) wahl += 1;
  if (zuletzt < 0) wahl = Math.floor(Math.random() * anzahl);
  try {
    sessionStorage.setItem(MERKER, String(wahl));
  } catch {}
  return wahl;
}

class JrUhr extends HTMLElement {
  async connectedCallback() {
    if (this.dataset.bereit) return;
    this.dataset.bereit = '1';
    const svg = this.querySelector('.jr-uhr__svg');
    if (svg && this.dataset.modelle) {
      try {
        const { MODELLE, zeichnen } = await import(this.dataset.modelle);
        const nr = modellWaehlen(MODELLE.length);
        if (nr > 0) svg.innerHTML = `<title id="jr-uhr-titel">${svg.querySelector('title')?.textContent ?? ''}</title>${zeichnen(MODELLE[nr])}`;
        this.dataset.modell = String(nr + 1);
      } catch (fehler) {
        console.warn('jr-uhr: Modell nicht geladen', fehler);
      }
    }
    this.stunde = this.querySelector('.jr-uhr__stunde');
    this.minute = this.querySelector('.jr-uhr__minute');
    this.sekunde = this.querySelector('.jr-uhr__sekunde');
    this.datum = this.querySelector('.jr-uhr__datum');
    this.zeit = this.querySelector('.jr-uhr__zeit');
    this.kippen = /** @type {HTMLElement | null} */ (this.querySelector('.jr-uhr__kippen'));
    this.ruhig = matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.format = new Intl.DateTimeFormat('de-DE', {
      timeZone: this.dataset.zeitzone || 'Europe/Berlin',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      day: 'numeric',
      hourCycle: 'h23',
    });
    this.classList.add('is-lebendig');

    this.ticken = () => {
      this.stellen();
      this.bild = this.ruhig ? 0 : requestAnimationFrame(this.ticken);
    };
    this.ticken();
    if (this.ruhig) this.takt = setInterval(() => this.stellen(), 15000);

    if (!this.ruhig && matchMedia('(hover: hover) and (pointer: fine)').matches && this.kippen) {
      this.bereich = this.closest('section') || this;
      this.beiMaus = (/** @type {PointerEvent} */ e) => {
        const r = this.getBoundingClientRect();
        const x = (e.clientX - (r.left + r.width / 2)) / (r.width / 2);
        const y = (e.clientY - (r.top + r.height / 2)) / (r.height / 2);
        const begrenzt = (v) => Math.max(-1, Math.min(1, v));
        this.kippen?.style.setProperty('--jr-kipp-y', `${(begrenzt(x) * 7).toFixed(2)}deg`);
        this.kippen?.style.setProperty('--jr-kipp-x', `${(begrenzt(-y) * 5).toFixed(2)}deg`);
      };
      this.beiVerlassen = () => {
        this.kippen?.style.setProperty('--jr-kipp-y', '0deg');
        this.kippen?.style.setProperty('--jr-kipp-x', '0deg');
      };
      this.bereich.addEventListener('pointermove', this.beiMaus, { passive: true });
      this.bereich.addEventListener('pointerleave', this.beiVerlassen);
    }
  }

  disconnectedCallback() {
    if (this.bild) cancelAnimationFrame(this.bild);
    if (this.takt) clearInterval(this.takt);
    if (this.bereich && this.beiMaus) {
      this.bereich.removeEventListener('pointermove', this.beiMaus);
      this.bereich.removeEventListener('pointerleave', this.beiVerlassen);
    }
  }

  stellen() {
    const jetzt = new Date();
    /** @type {Record<string, string>} */
    const t = {};
    for (const teil of this.format.formatToParts(jetzt)) t[teil.type] = teil.value;
    const h = Number(t.hour);
    const m = Number(t.minute);
    const s = Number(t.second) + jetzt.getMilliseconds() / 1000;
    const sek = this.ruhig ? Math.floor(s) : s;
    this.drehen(this.stunde, (h % 12) * 30 + m * 0.5 + sek / 120);
    this.drehen(this.minute, m * 6 + sek * 0.1);
    this.drehen(this.sekunde, sek * 6);
    if (this.datum && this.datum.textContent !== t.day) this.datum.textContent = t.day;
    const text = `${t.hour}:${t.minute} Uhr`;
    if (this.zeit && this.zeit.textContent !== text) this.zeit.textContent = text;
  }

  /** @param {Element | null} el @param {number} grad */
  drehen(el, grad) {
    if (!(el instanceof SVGElement)) return;
    el.setAttribute('transform', `rotate(${grad.toFixed(2)} ${el.dataset.cx ?? 200} ${el.dataset.cy ?? 300})`);
  }
}

if (!customElements.get('jr-uhr')) customElements.define('jr-uhr', JrUhr);
